/**
 * Live pricing maths for the create-plan modal.
 *
 * Every threshold here — band table, floors, interval caps and FX rates —
 * arrives from the API as `planManager.planPricing`. Nothing is hard-coded, so
 * the modal can never disagree with the floor the server enforces at save time.
 * These results are UX only; the API re-checks the price before creating the
 * Stripe price.
 */

const DAYS_IN_YEAR = 365;

const roundCents = (value) => Math.round(value * 100) / 100;

/**
 * Converts an amount to its USD equivalent using units-per-USD rates. An
 * unknown currency falls back to 1:1, matching the API helper.
 */
export const toUsd = (amount, currency, fxRates) => {
  const code = String(currency || "USD").toUpperCase();
  if (code === "USD") return amount;
  return amount / (fxRates?.[code] ?? 1);
};

/**
 * Converts a USD amount into a given currency — the inverse of `toUsd`. The
 * floor/below-floor check stays USD internally; this is only for quoting
 * that USD-denominated threshold back in the currency the coach is pricing
 * in, so it doesn't read as if it's still in dollars.
 */
export const fromUsd = (amount, currency, fxRates) => {
  const code = String(currency || "USD").toUpperCase();
  if (code === "USD") return amount;
  return amount * (fxRates?.[code] ?? 1);
};

/**
 * Length of one billing cycle in days.
 */
export const getCycleDays = (interval, intervalCount, intervalDays) => {
  const count = Number(intervalCount) || 1;
  if (interval === "quarter") return (intervalDays?.month ?? 30) * 3;
  const days = intervalDays?.[interval];
  return days ? days * count : (intervalDays?.month ?? 30) * count;
};

/**
 * Clamps a custom interval count to Stripe's one-year maximum billing span.
 */
export const clampIntervalCount = (interval, intervalCount, intervalCaps) => {
  const count = Math.floor(Number(intervalCount)) || 1;
  const cap = intervalCaps?.[interval];
  if (!cap) return Math.max(count, 1);
  return Math.min(Math.max(count, 1), cap);
};

/**
 * Checks a price against its product's floor and returns everything the
 * summary rail needs to render. The normalized equivalent spreads a year of
 * billing cycles across the band's periods, so it always agrees with the
 * annual revenue shown beside it.
 *
 * @param plan {{product: string, price: number|string, currency: string, interval: string, intervalCount: number, billingType?: string}}
 * @param planPricing {{floors: Object, intervalDays: Object, intervalCaps: Object, fxRates: Object}}
 */
export const getPlanSummary = (plan, planPricing) => {
  const { product, price, currency, interval, intervalCount, billingType } =
    plan;
  const { floors, intervalDays, fxRates } = planPricing || {};

  const amount = Number(price);
  const hasPrice = Number.isFinite(amount) && amount > 0;
  const usdPrice = hasPrice ? roundCents(toUsd(amount, currency, fxRates)) : 0;

  const isOneOff = billingType === "one_off";
  const cycleDays = isOneOff
    ? null
    : getCycleDays(interval, intervalCount, intervalDays);
  // Whole cycles only — a member is billed 12 times on a monthly plan, not
  // 365/30 times, and the annual revenue has to match the count shown.
  const cyclesPerYear = cycleDays ? Math.floor(DAYS_IN_YEAR / cycleDays) : 0;
  // A cadence longer than a year would floor to zero cycles; keep the fraction
  // so the equivalent stays a real number. Only reachable before the interval
  // count is clamped, since the caps hold a plan to a one-year span.
  const equivCycles =
    cycleDays && cyclesPerYear < 1 ? DAYS_IN_YEAR / cycleDays : cyclesPerYear;

  const summary = {
    hasPrice,
    usdPrice,
    isOneOff,
    cycleDays,
    cyclesPerYear,
    annualRevenue: hasPrice ? roundCents(amount * cyclesPerYear) : 0,
    band: null,
    normalizedEquiv: usdPrice,
    // The `Display` fields mirror their USD counterparts in the plan's own
    // currency, purely for the rail — the `below` check above stays USD.
    normalizedEquivDisplay: hasPrice ? amount : 0,
    floor: null,
    floorDisplay: null,
    below: false,
  };

  // PRESS and every one-off pass sit on a flat per-charge floor.
  if (product !== "push" || isOneOff) {
    summary.floor = floors?.press ?? null;
    summary.floorDisplay =
      summary.floor !== null
        ? roundCents(fromUsd(summary.floor, currency, fxRates))
        : null;
    summary.below =
      hasPrice && summary.floor !== null && usdPrice < summary.floor;
    return summary;
  }

  const band = floors?.push?.find(
    ({ maxCycleDays }) => maxCycleDays === null || cycleDays <= maxCycleDays
  );
  if (!band) return summary;

  summary.band = band.band;
  summary.floor = band.floor;
  summary.floorDisplay = roundCents(fromUsd(band.floor, currency, fxRates));
  // `planPricing` is cached for two hours, so for a while after a deploy the
  // modal can be handed a band table from before periodsPerYear existed. Fall
  // back to the day-normalized form it shipped with rather than rendering NaN.
  summary.normalizedEquiv = roundCents(
    band.periodsPerYear
      ? (usdPrice * equivCycles) / band.periodsPerYear
      : (usdPrice * band.normalizeDays) / cycleDays
  );
  // Computed from the local `amount` directly rather than converting
  // `normalizedEquiv` back — one FX hop instead of two.
  summary.normalizedEquivDisplay = roundCents(
    band.periodsPerYear
      ? (amount * equivCycles) / band.periodsPerYear
      : (amount * band.normalizeDays) / cycleDays
  );
  summary.below = hasPrice && summary.normalizedEquiv < band.floor;
  return summary;
};
